import { Button, Card, CardBody, Divider, FormControl, FormErrorMessage, FormHelperText, FormLabel, HStack, Input, Stack, Textarea, useToast } from "@chakra-ui/react";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { createRole, deleteRole } from "service/api/role.api";
import { insertRole, removeRole, updateTmpRole } from "store/reducers/role.slice";
import { useAppDispatch } from "store/store.hook";
import { PermissionModalMode } from "types/PermissionModalMode";
import { TemplateModalMode } from "types/TemplateModalMode";

type RoleFormInfoProps = {
  modalMode: PermissionModalMode;
  roleTitle: string;
  roleDescription: string;
  roleCode: string;
  roleId: string;
  onClose: () => any;
};

/**
 * Component for displaying and handling role form information in a modal.
 * 
 * @param {Object} props - The properties object.
 * @param {PermissionModalMode} props.modalMode - The mode of the modal (Create, View, Delete).
 * @param {string} props.roleId - The ID of the role.
 * @param {string} props.roleTitle - The title of the role.
 * @param {string} props.roleDescription - The description of the role.
 * @param {string} props.roleCode - The code of the role.
 * @param {Function} props.onClose - The function to call when the modal is closed.
 * 
 * @returns {JSX.Element} The rendered component.
 */

export default function RoleFormInfo({ modalMode, roleId, roleTitle, roleDescription, roleCode, onClose }: RoleFormInfoProps) {

  const toast = useToast()
  const dispatch = useAppDispatch();
  const { register, trigger, handleSubmit, watch } = useForm({
    defaultValues: {
      "roleTitle": roleTitle,
      "roleDescription": roleDescription,
      "roleCode": roleCode
    },
  });
  const [isFieldReadOnly, setIsFieldReadOnly] = useState<boolean>(modalMode === PermissionModalMode.View || modalMode === PermissionModalMode.Delete);

  watch((data, { name, type }) => {
    dispatch(
      updateTmpRole({ ...data })
    )

  });

  const [isCodeWrong, setIsCodeWrong] = useState<boolean>(false);
  const handleInputCodeChange = (e: any) => {

    const code = e.target.value;
    if (code && code.match(/^[A-Za-z0-9]{1,64}$/
    ) || code == "") {
      setIsCodeWrong(false);
    } else {
      setIsCodeWrong(true);
    }

  }


  const onSubmit: SubmitHandler<any> = (data) => {

    if (modalMode === PermissionModalMode.Create) {
      createRole(data.roleTitle, data.roleCode, data.roleDescription).then((res) => {
        dispatch(insertRole(res));
        onClose();
        toast({
          title: 'Ruolo creato.',
          description: "Aggiunto ruolo, completare la configurazione dei permessi.",
          status: 'success',
          duration: 9000,
          isClosable: true,
          position: "top"
        })
      })
    } else if (modalMode === PermissionModalMode.Delete) {

      deleteRole(roleId).then((res) => {
        dispatch(removeRole({ _id: roleId }));
        onClose();
        toast({
          title: 'Ruolo rimosso.',
          description: "",
          status: 'success',
          duration: 9000,
          isClosable: true,
          position: "top"

        })
      }).catch((err) => {
        toast({
          title: 'Errore.',
          description: "Impossibile eliminare il ruolo, controllare che non ci siano utenti associati.",
          status: 'error',
          duration: 9000,
          isClosable: true,
          position: "top"

        })
      })
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>

        {modalMode != PermissionModalMode.Create ?
          <>
            <Card variant={"filled"}>
              <CardBody>

                <HStack>
                  <FormControl mb={5}>
                    <FormLabel>Codice</FormLabel>
                    <Input placeholder="Inserisci il codice" size={"xs"}
                      isRequired={true}
                      isReadOnly={true}
                      {
                      ...register(`roleCode`, { required: true })
                      }
                    />
                  </FormControl>

                  <FormControl mb={5}>
                    <FormLabel>Titolo ruolo</FormLabel>
                    <Input placeholder="Inserisci il titolo del ruolo" size={"xs"}
                      isRequired={true}
                      isReadOnly={true}
                      {
                      ...register(`roleTitle`, { required: true })
                      }
                    />
                  </FormControl>

                </HStack>

                <FormControl mb={5}>
                  <FormLabel>Descrizione ruolo</FormLabel>
                  <Textarea placeholder="Inserisci il codice" size={"xs"}
                    isRequired={true}
                    isReadOnly={true}
                    {
                    ...register(`roleDescription`, { required: true })
                    }
                  />
                </FormControl>

              </CardBody>
            </Card>
            <Divider my={2} />
          </>
          :
          <>        <FormControl mb={5} isRequired={true}>
            <FormLabel>Titolo ruolo</FormLabel>
            <Input placeholder='Inserisci il nome del nuovo ruolo' size='md' required={true}
              isReadOnly={isFieldReadOnly}
              isRequired={true}
              {
              ...register(`roleTitle`, { required: true })
              }
            />
          </FormControl>

            <FormControl mb={5} isRequired={true} isInvalid={isCodeWrong}>
              <FormLabel htmlFor="input-code" display={"inline"}>Codice</FormLabel><FormHelperText display={"inline"}>(solo valori alfanumerici ed univoci)</FormHelperText>
              <Input placeholder='Inserisci il codice del nuovo ruolo' size='md'
                id="input-code"
                pattern="[A-Za-z0-9]{1,32}"
                isReadOnly={isFieldReadOnly}
                isRequired={true}
                {
                ...register(`roleCode`, { required: true })
                }
                onChangeCapture={handleInputCodeChange}
                errorBorderColor="red.300"
                _invalid={
                  {
                    borderColor: "red.300",
                    bg: "rgba(255, 0, 0, 0.1)",
                  }
                }
              />
              {
                isCodeWrong &&
                <FormErrorMessage>
                  Il codice deve essere composto da sole lettere e numeri e non deve essere vuoto.
                </FormErrorMessage>
              }
            </FormControl>


            <FormControl mb={5} isRequired={true}>
              <FormLabel>Descrizione ruolo</FormLabel>
              <Input placeholder='Inserisci una breve descrizione del nuovo ruolo' size='md'
                isRequired={true}
                isReadOnly={isFieldReadOnly}
                {
                ...register(`roleDescription`, { required: true })
                }
              />
            </FormControl>
          </>

        }


        {
          modalMode === PermissionModalMode.Create &&
          <Button variant={"solid"} colorScheme="green" type="submit" width={"full"}>
            Salva nuovo ruolo
          </Button>
        }
        {
          modalMode === PermissionModalMode.Delete &&
          <Button variant={"solid"} colorScheme="red" type="submit" width={"full"}>
            Conferma eliminazione ruolo
          </Button>
        }
      </form>
    </>
  );
}
